import useGiphyDialog from '@tenant/recognition/RecognitionModal/sendRecognitionView/giphySearch/useGiphyDialog'
import { useCallback, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

// NOTE: The `insertGif` Froala command itself is defined in froalaCustomCommands,
// which runs from the lazily-loaded editor chunk so `froala-editor` stays out of
// the initial bundle. This hook only wires the button's click callback to the
// GIF dialog for the active editor instance.

type UseFroalaGifsProps = {
  fieldName?: string
  modalId?: string
  /**
   * When false, the callback below is left untouched: the button is absent from
   * this editor's toolbar, and the callback is a single global slot shared with
   * whichever other editor on the page does offer GIFs.
   */
  isEnabled?: boolean
}

export const useFroalaGifs = ({
  fieldName = 'gif',
  modalId = 'gifModal',
  isEnabled = true,
}: UseFroalaGifsProps = {}) => {
  const giphyDialogId = useMemo(
    () => `rich-text-editor-${fieldName}-${modalId}`,
    [fieldName, modalId],
  )
  const { openGiphyDialog } = useGiphyDialog(giphyDialogId)
  const { setValue } = useFormContext()

  // Claims the GIF button's global click callback for this editor instance.
  const registerGifButtonCommand = useCallback(() => {
    if (isEnabled && typeof window !== 'undefined') {
      window.__froalaGifButtonCallback = () => openGiphyDialog()
    }
  }, [isEnabled, openGiphyDialog])

  useEffect(() => {
    if (!isEnabled) return

    registerGifButtonCommand()

    return () => {
      if (typeof window !== 'undefined') {
        window.__froalaGifButtonCallback = undefined
      }
    }
  }, [isEnabled, registerGifButtonCommand])

  const handleGifSelected = (url: string) => {
    setValue(fieldName, url, { shouldValidate: true, shouldDirty: true })
  }

  return {
    giphyDialogId,
    handleGifSelected,
    registerGifButtonCommand,
  }
}

export default useFroalaGifs
